import { type FC, useEffect, useMemo } from "react";
import type { AgentMessageFragmentNextStepsProposal } from "@/hooks/use-stream-conversation";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { PromptInputModifierEnterKeyBinding } from "@/utils/platform";
import { isTouchDevice } from "@/utils/is-touch-device";
import {
    ALLOWED_NEXT_STEP_TITLES,
    getPrimaryNextStepAction,
    normalizeActionTitle,
} from "@/components/ai/next-step-allowlist";

type FragmentNextStepsProposalProps = {
    fragment: AgentMessageFragmentNextStepsProposal;
    onActionClick?: (prompt: string) => void;
};

export const FragmentNextStepsProposal: FC<FragmentNextStepsProposalProps> = ({ fragment, onActionClick }) => {
    const allowedActions = useMemo(
        () => fragment.actions.filter((a) => ALLOWED_NEXT_STEP_TITLES.has(normalizeActionTitle(a.title))),
        [fragment.actions],
    );

    const primaryAction = useMemo(() => getPrimaryNextStepAction(allowedActions), [allowedActions]);
    // Limit to at most 1 secondary action (2 total buttons) to prevent overflow.
    const secondaryActions = useMemo(
        () => allowedActions.filter((action) => action !== primaryAction).slice(0, primaryAction ? 1 : 2),
        [allowedActions, primaryAction],
    );

    useEffect(() => {
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            // Skip when focus is in a text-editable element (e.g., chat textarea) to avoid
            // conflicting with its own Cmd+Enter handler.
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === "TEXTAREA" || tag === "INPUT") return;

            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
                e.preventDefault();
                if (primaryAction && onActionClick) {
                    onActionClick(primaryAction.prompt);
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, [primaryAction, onActionClick]);

    if (allowedActions.length === 0) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-t-lg border border-border-base bg-surface-primary shadow-sm">
            <div className="flex items-center justify-end gap-2 px-4 pb-5 pt-3">
                {secondaryActions.map((action, index) => (
                    <Button
                        key={`${action.title}-${index}`}
                        variant="ghost"
                        size="sm"
                        onClick={() => onActionClick?.(action.prompt)}
                        data-tracking-id="next-steps-proposal-secondary-action"
                    >
                        {action.title}
                    </Button>
                ))}
                {primaryAction && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onActionClick?.(primaryAction.prompt)}
                        data-tracking-id="next-steps-proposal-primary-action"
                    >
                        {primaryAction.title}
                        {!isTouchDevice() && (
                            <span className="ml-1 shrink-0 opacity-60">
                                <KeyboardCombo
                                    keys={PromptInputModifierEnterKeyBinding}
                                    className="bg-content-primary-inverted/20 text-content-primary-inverted"
                                />
                            </span>
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
};
